import React, { useState } from 'react';
import { X, UserPlus, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { adminApi } from '@/services/api/admin';
import type { UserData } from '@/services/types/domain';

interface Props {
    open: boolean;
    onClose: () => void;
    onCreated?: (user?: UserData) => void;
}

type Payload = {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    phone?: string;
    date_of_birth?: string; // YYYY-MM-DD
    user_type: 'admin' | 'customer' | 'seller';
    password: string;
    password_confirmation?: string;
};

const AdminCreateUserModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
    const [form, setForm] = useState<Payload>({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        date_of_birth: '',
        user_type: 'customer',
        password: '',
        password_confirmation: '',
    });
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showPwd, setShowPwd] = useState(false);
    const [showPwd2, setShowPwd2] = useState(false);

    if (!open) return null;

    const validate = () => {
        const e: Record<string, string> = {};
        if (!form.username.trim()) e.username = 'Requerido';
        if (!form.email.trim()) e.email = 'Requerido';
        if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email inválido';
        if (!form.first_name.trim()) e.first_name = 'Requerido';
        if (!form.last_name.trim()) e.last_name = 'Requerido';
        if (!form.user_type) e.user_type = 'Requerido';
        if (!form.password || form.password.length < 8) e.password = 'Mínimo 8 caracteres';
        if ((form.password_confirmation ?? '') !== form.password) e.password_confirmation = 'Las contraseñas no coinciden';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        setBusy(true);
        try {
            const payload: Payload = {
                username: form.username.trim(),
                email: form.email.trim(),
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                phone: form.phone?.trim() || undefined,
                date_of_birth: form.date_of_birth || undefined,
                user_type: form.user_type,
                password: form.password,
                password_confirmation: form.password_confirmation,
            };

            const created = await adminApi.createUser(payload as any);
            toast.success('Usuario creado correctamente');
            onCreated?.(created as UserData);
            onClose();
        } catch (err: any) {
            const apiMsg = err?.response?.data?.message;
            const apiErrors = err?.response?.data?.errors;
            if (apiErrors && typeof apiErrors === 'object') {
                const mapped: Record<string, string> = {};
                Object.keys(apiErrors).forEach((k) => {
                    const first = Array.isArray(apiErrors[k]) ? apiErrors[k][0] : apiErrors[k];
                    mapped[k] = String(first || 'Inválido');
                });
                setErrors(mapped);
            }
            toast.error(apiMsg || err?.message || 'No se pudo crear el usuario');
        } finally {
            setBusy(false);
        }
    };

    const onChange =
        (k: keyof Payload) =>
            (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative z-[61] w-full max-w-xl rounded-2xl border bg-white shadow-xl">
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <div className="flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-primary-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Nuevo Usuario</h3>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-50">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                        label="Username"
                        placeholder="jdoe"
                        value={form.username}
                        onChange={onChange('username')}
                        error={errors.username}
                        fullWidth
                    />
                    <Input
                        label="Email"
                        placeholder="usuario@correo.com"
                        value={form.email}
                        onChange={onChange('email')}
                        error={errors.email}
                        fullWidth
                    />
                    <Input
                        label="Nombre"
                        placeholder="Juan"
                        value={form.first_name}
                        onChange={onChange('first_name')}
                        error={errors.first_name}
                        fullWidth
                    />
                    <Input
                        label="Apellido"
                        placeholder="Pérez"
                        value={form.last_name}
                        onChange={onChange('last_name')}
                        error={errors.last_name}
                        fullWidth
                    />
                    <Input
                        label="Teléfono"
                        placeholder="+51 999 999 999"
                        value={form.phone}
                        onChange={onChange('phone')}
                        fullWidth
                    />

                    {/* Fecha de nacimiento */}
                    <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-gray-700">Fecha de nacimiento</label>
                        <input
                            type="date"
                            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={form.date_of_birth}
                            onChange={onChange('date_of_birth')}
                        />
                    </div>

                    {/* Tipo de usuario */}
                    <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-gray-700">Tipo de usuario</label>
                        <select
                            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={form.user_type}
                            onChange={onChange('user_type')}
                        >
                            <option value="customer">Cliente</option>
                            <option value="seller">Vendedor</option>
                            <option value="admin">Administrador</option>
                        </select>
                    </div>

                    {/* Contraseña */}
                    <div className="sm:col-span-2">
                        <label className="mb-1 text-sm font-medium text-gray-700">Contraseña</label>
                        <div className="relative">
                            <input
                                type={showPwd ? 'text' : 'password'}
                                className={`w-full rounded-md border border-input bg-background px-3 py-2 text-sm ${errors.password ? 'border-red-500' : ''
                                    }`}
                                value={form.password}
                                onChange={onChange('password')}
                                placeholder="Mínimo 8 caracteres"
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPwd((s) => !s)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                                title={showPwd ? 'Ocultar' : 'Mostrar'}
                            >
                                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
                    </div>

                    {/* Confirmación */}
                    <div className="sm:col-span-2">
                        <label className="mb-1 text-sm font-medium text-gray-700">Confirmar contraseña</label>
                        <div className="relative">
                            <input
                                type={showPwd2 ? 'text' : 'password'}
                                className={`w-full rounded-md border border-input bg-background px-3 py-2 text-sm ${errors.password_confirmation ? 'border-red-500' : ''
                                    }`}
                                value={form.password_confirmation}
                                onChange={onChange('password_confirmation')}
                                placeholder="Repite la contraseña"
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPwd2((s) => !s)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                                title={showPwd2 ? 'Ocultar' : 'Mostrar'}
                            >
                                {showPwd2 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        {errors.password_confirmation && (
                            <p className="mt-1 text-xs text-red-600">{errors.password_confirmation}</p>
                        )}
                    </div>

                    <div className="sm:col-span-2 flex items-center justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={busy}>
                            {busy ? 'Creando…' : 'Crear'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminCreateUserModal;
